import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, FONT_DISPLAY, FONT_MONO, SURFACE, TEXT_PRIMARY, TEXT_SECONDARY } from '../../tokens';

interface Layer {
  label: string;
  title: string;
  body: string;
}

const LAYERS: readonly Layer[] = [
  {
    label: 'LAYER 01, DIRECTIVE',
    title: 'The What',
    body: 'SOPs, business logic, the way you actually want things done. Lives in plain Markdown inside your Enviorment. You can read it, edit it, hand it to a new employee.',
  },
  {
    label: 'LAYER 02, ORCHESTRATION',
    title: 'The Decision-Making',
    body: 'Claude agents that route work, ask clarifying questions, and choose which tool to use for which job. This is the only layer the AI is fully in charge of, because it’s the only layer where judgment matters more than precision.',
  },
  {
    label: 'LAYER 03, EXECUTION',
    title: 'The Doing',
    body: 'Deterministic Python scripts and direct API calls that handle anything code can do more reliably than language. The AI never does what code can do better.',
  },
];

export function HowIWorkSection() {
  const isMobile = useIsMobile();

  return (
    <section
      style={{
        padding: isMobile ? '60px 20px' : '100px 64px',
        background: 'rgba(6,6,6,0.88)',
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <SectionLabel number="03">How I Work</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>Three layers. No black boxes.</SectionH2>
            <p style={{ color: TEXT_SECONDARY, maxWidth: 600, margin: '0 auto', lineHeight: 1.75 }}>
              Every system runs on three layers, and the reason it&rsquo;s structured this way is that if
              you try to make AI do everything itself, errors compound, ninety percent accuracy per step
              turns into fifty-nine percent success across five steps, which is why most
              &ldquo;agentic&rdquo; setups fall apart the moment they leave the demo.
            </p>
          </div>
        </FadeUp>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {LAYERS.map((layer, i) => (
            <FadeUp key={layer.label} delay={80 + i * 60}>
              <div
                style={{
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 20,
                  padding: isMobile ? '24px 20px' : '32px 28px',
                  borderLeft: `3px solid ${ACCENT}`,
                }}
              >
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: '0.7rem',
                    color: ACCENT,
                    letterSpacing: '0.12em',
                    marginBottom: 10,
                  }}
                >
                  {layer.label}
                </div>
                <h3
                  style={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: 'clamp(1.2rem,1.8vw,1.5rem)',
                    fontWeight: 400,
                    lineHeight: 1.1,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: TEXT_PRIMARY,
                    marginBottom: 10,
                  }}
                >
                  {layer.title}
                </h3>
                <p style={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.75 }}>{layer.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={260}>
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <p style={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.75, maxWidth: 640, margin: '0 auto' }}>
              The result is a system where AI makes smart decisions on top of reliable rails, instead of
              being asked to do everything from scratch every time, which is what most setups get wrong,
              and why most setups fail.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
